import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AppStore, AuthService } from '@core/services';
import { take } from 'rxjs/operators';

/**
 * Componente per gestire la root route (/).
 * Secondo la documentazione SSO:
 * - /?target_app=xxx → Redirect a /sso-fast?target_app=xxx (gestito da SsoFast)
 * - / (senza target_app) → Se c'è token: verifica e redirect a /landing-page, altrimenti redirect a /login
 */
@Component({
  selector: 'app-root-redirect',
  template: '', // Componente vuoto, fa solo redirect
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: true
})
export class RootRedirect implements OnInit {
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly appStore = inject(AppStore);
  private readonly authService = inject(AuthService);

  ngOnInit(): void {
    // Leggi i query params dalla route
    const targetApp = this.activatedRoute.snapshot.queryParams['target_app'] as string | undefined;

    if (targetApp) {
      // Root route con target_app: redirect a /sso-fast (gestito da SsoFast per performance migliori)
      this.router.navigate(['/sso-fast'], { queryParams: { target_app: targetApp } });
    } else {
      // Root route senza target_app: secondo la documentazione SSO
      // Se c'è un token salvato (indipendentemente da rememberMe): verifica autenticazione e redirect a /landing-page
      // Se non c'è token o il token non è valido: redirect a /login
      const token = this.appStore.token();
      const isAuthenticated = this.appStore.isAuthenticated();

      if (token) {
        if (isAuthenticated) {
          // Token valido e utente autenticato: redirect a landing-page
          this.router.navigateByUrl('/landing-page');
        } else {
          // C'è un token ma non l'user: verifica se il token è ancora valido
          this.authService.me().pipe(take(1)).subscribe({
            next: () => {
              // Token valido, redirect a landing-page
              this.router.navigateByUrl('/landing-page');
            },
            error: () => {
              // Token non valido o scaduto, redirect a /login
              this.router.navigateByUrl('/login');
            }
          });
        }
      } else {
        // Non c'è token, redirect a /login
        this.router.navigateByUrl('/login');
      }
    }
  }
}
